/** Cognito groups for Respaldos isolation */
export const COGNITO_GROUPS = [
  'Marketing',
  'BackupsBO',
  'BackupsPE',
  'BackupsPY',
  'Admins',
] as const;

export type CognitoGroup = (typeof COGNITO_GROUPS)[number];

export type SpaceId = 'marketing' | 'bo' | 'pe' | 'py';

export interface SpaceDefinition {
  id: SpaceId;
  label: string;
  hint?: string;
  /** Cognito groups that may access this space */
  allowedGroups: CognitoGroup[];
  bucketEnvKey: string;
  /** Default S3 prefix (must end with / when non-empty) */
  prefix: string;
  readOnly: boolean;
  kind: 'marketing' | 'backup';
}

export const SPACES: Record<SpaceId, SpaceDefinition> = {
  marketing: {
    id: 'marketing',
    label: 'Marketing',
    allowedGroups: ['Marketing', 'Admins'],
    bucketEnvKey: 'MARKETING_BUCKET',
    prefix: 'marketing/',
    readOnly: false,
    kind: 'marketing',
  },
  bo: {
    id: 'bo',
    label: 'Respaldos · Bolivia',
    hint: 'BO',
    allowedGroups: ['BackupsBO', 'Admins'],
    bucketEnvKey: 'BACKUP_BO_BUCKET',
    prefix: 'db/',
    readOnly: true,
    kind: 'backup',
  },
  pe: {
    id: 'pe',
    label: 'Respaldos · Perú',
    hint: 'PE',
    allowedGroups: ['BackupsPE', 'Admins'],
    bucketEnvKey: 'BACKUP_PE_BUCKET',
    prefix: 'db/',
    readOnly: true,
    kind: 'backup',
  },
  py: {
    id: 'py',
    label: 'Respaldos · Paraguay',
    hint: 'PY',
    allowedGroups: ['BackupsPY', 'Admins'],
    bucketEnvKey: 'BACKUP_PY_BUCKET',
    prefix: 'db/',
    readOnly: true,
    kind: 'backup',
  },
};

export const AWS_ACCOUNT = '656567185219';
export const AWS_REGION = 'us-east-1';

/** Known BO backup prefixes under db/ */
export const BO_DB_PREFIXES = [
  'KAPPA',
  'MOTOR-SIAT',
  'Novus',
  'Varios',
  'catercu',
  'detrocummins',
  'komatcu',
] as const;

export const DEFAULT_BUCKETS = {
  MARKETING_BUCKET: 'galero-respaldos-marketing-656567185219',
  BACKUP_BO_BUCKET: 'prod-bo-backups',
  BACKUP_PE_BUCKET: 'prod-pe-backups',
  BACKUP_PY_BUCKET: 'prod-py-backups',
} as const;

/**
 * Resolve which spaces a user may access from Cognito groups.
 * Marketing must NEVER receive backup spaces.
 */
export function spacesForGroups(groups: readonly string[]): SpaceDefinition[] {
  const set = new Set(groups);
  return (Object.values(SPACES) as SpaceDefinition[]).filter((space) =>
    space.allowedGroups.some((g) => set.has(g)),
  );
}

export function canAccessSpace(
  groups: readonly string[],
  spaceId: SpaceId,
): boolean {
  const space = SPACES[spaceId];
  if (!space) return false;
  return space.allowedGroups.some((g) => groups.includes(g));
}

/**
 * Normalize and validate a relative path under a space prefix.
 * Rejects path traversal and absolute keys.
 */
export function resolveObjectKey(
  space: SpaceDefinition,
  relativePath: string,
): string {
  const cleaned = (relativePath || '')
    .replace(/^\/+/, '')
    .replace(/\\/g, '/');
  if (cleaned.includes('..')) {
    throw new AccessDeniedError('Ruta inválida (path traversal)');
  }
  // Reject attempts to escape marketing/ into sibling prefixes
  if (cleaned.startsWith('backups/') || cleaned.startsWith('prod-')) {
    throw new AccessDeniedError('Prefijo no permitido');
  }
  const prefix = space.prefix;
  if (!cleaned) return prefix;
  return `${prefix}${cleaned}`.replace(/\/{2,}/g, '/');
}

/**
 * Ensure an absolute object key stays inside the space prefix.
 * Rejects path traversal and keys that escape the allowed prefix.
 */
export function assertKeyInSpace(space: SpaceDefinition, key: string): void {
  const normalized = (key || '').replace(/\\/g, '/');
  if (!normalized || normalized.includes('..')) {
    throw new AccessDeniedError('Clave fuera del prefijo permitido');
  }
  if (!normalized.startsWith(space.prefix)) {
    throw new AccessDeniedError('Clave fuera del prefijo permitido');
  }
}

/**
 * Sanitize a single folder segment name (no slashes, no traversal).
 */
export function sanitizeFolderName(name: string): string {
  const cleaned = (name || '').trim().replace(/\\/g, '/');
  if (!cleaned || cleaned.includes('/') || cleaned.includes('..')) {
    throw new AccessDeniedError('Nombre de carpeta inválido');
  }
  if (cleaned === '.' || cleaned === '_trash') {
    throw new AccessDeniedError('Nombre de carpeta no permitido');
  }
  return cleaned;
}

export function assertSpaceAccess(
  groups: readonly string[],
  spaceId: SpaceId,
): SpaceDefinition {
  if (!canAccessSpace(groups, spaceId)) {
    throw new AccessDeniedError(
      `Acceso denegado al espacio "${spaceId}". Grupos: [${groups.join(', ')}]`,
    );
  }
  return SPACES[spaceId];
}

export class InvalidPersonNameError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidPersonNameError';
  }
}

/** Cognito attributes for an invited user. `name` is given + family. */
export function cognitoPersonName(
  givenName: string,
  familyName: string,
): { given_name: string; family_name: string; name: string } {
  const given_name = givenName.trim();
  const family_name = familyName.trim();
  if (!given_name || !family_name) {
    throw new InvalidPersonNameError('Nombre y apellido son requeridos');
  }
  if (given_name.length > 80 || family_name.length > 80) {
    throw new InvalidPersonNameError(
      'Nombre y apellido deben tener como máximo 80 caracteres',
    );
  }
  return {
    given_name,
    family_name,
    name: `${given_name} ${family_name}`.trim(),
  };
}

export class AccessDeniedError extends Error {
  readonly statusCode = 403;
  constructor(message: string) {
    super(message);
    this.name = 'AccessDeniedError';
  }
}

export type AuditAction =
  | 'upload'
  | 'download'
  | 'delete'
  | 'restore'
  | 'mkdir'
  | 'move'
  | 'list'
  | 'archive';

export interface AuditEvent {
  eventId: string;
  spaceId: SpaceId;
  action: AuditAction;
  actorSub: string;
  actorName: string;
  actorEmail?: string;
  objectKey: string;
  detail?: string;
  createdAt: string;
}

export type StorageTier = 'hot' | 'auto' | 'archive' | 'deep';

export function storageClassToTier(
  storageClass?: string | null,
): StorageTier | null {
  if (!storageClass) return 'hot';
  switch (storageClass) {
    case 'STANDARD':
    case 'STANDARD_IA':
    case 'ONEZONE_IA':
    case 'REDUCED_REDUNDANCY':
      return 'hot';
    case 'INTELLIGENT_TIERING':
      return 'auto';
    case 'GLACIER_IR':
    case 'GLACIER':
      return 'archive';
    case 'DEEP_ARCHIVE':
      return 'deep';
    default:
      return 'hot';
  }
}
